<template>
  <el-dialog
    title="选择班级"
    :visible.sync="dialogVisible"
    width="1100px"
    :close-on-click-modal="false"
    append-to-body
    @close="handleClose"
  >
    <!-- 筛选区 -->
    <div class="filter-section">
      <el-form :inline="true" :model="filterForm" size="small">
        <el-form-item label="校区">
          <el-select
            v-model="filterForm.schoolAreaId"
            placeholder="全部校区"
            clearable
            filterable
            style="width: 160px;"
          >
            <el-option label="全部" :value="0"></el-option>
            <el-option
              v-for="item in schoolAreaList"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="班级名称">
          <el-input
            v-model="filterForm.name"
            placeholder="请输入班级名称"
            clearable
            style="width: 200px;"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>

        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 已选班级展示（多选模式） -->
    <div v-if="multiple && selectedList.length > 0" class="selected-section">
      <div class="selected-header">
        <span class="selected-title">已选班级 ({{ selectedList.length }})</span>
        <el-button type="text" size="small" @click="clearSelection">清空</el-button>
      </div>
      <div class="selected-tags">
        <el-tag
          v-for="item in selectedList"
          :key="item.id"
          closable
          size="small"
          @close="removeSelected(item)"
        >
          {{ item.name }}
        </el-tag>
      </div>
    </div>

    <!-- 表格区 -->
    <div class="table-section">
      <el-table
        ref="classTable"
        :data="tableData"
        v-loading="tableLoading"
        stripe
        border
        height="400px"
        highlight-current-row
        @row-dblclick="handleRowDblClick"
        @selection-change="handleSelectionChange"
      >
        <!-- 多选模式显示选择列 -->
        <el-table-column
          v-if="multiple"
          type="selection"
          width="50"
          align="center"
        />

        <el-table-column
          prop="name"
          label="班级名称"
          min-width="120"
          show-overflow-tooltip
        />

        <el-table-column
          prop="course.name"
          label="课程名称"
          min-width="120"
          show-overflow-tooltip
        />

        <el-table-column
          label="授课教师"
          min-width="100"
          show-overflow-tooltip
        >
          <template slot-scope="scope">
            <span v-if="scope.row.teacher">{{ scope.row.teacher.name }}</span>
            <span v-else-if="scope.row.schoolTeacher">{{ scope.row.schoolTeacher.name }}</span>
            <span v-else class="text-gray">-</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="schoolArea.name"
          label="校区"
          min-width="100"
          show-overflow-tooltip
        >
          <template slot-scope="scope">
            <span v-if="scope.row.schoolArea">{{ scope.row.schoolArea.name }}</span>
            <span v-else class="text-gray">-</span>
          </template>
        </el-table-column>

        <el-table-column
          label="名额"
          width="120"
          align="center"
        >
          <template slot-scope="scope">
            <div class="quota-cell">
              <el-progress
                :percentage="getQuotaPercentage(scope.row)"
                :color="getQuotaColor(scope.row)"
                :stroke-width="8"
              />
              <span class="quota-text">{{ scope.row.studentCount || 0 }}/{{ scope.row.enrollCount || 0 }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="操作"
          width="80"
          align="center"
          fixed="right"
        >
          <template slot-scope="scope">
            <el-button
              v-if="!isSelected(scope.row)"
              type="text"
              size="small"
              @click="handleSelect(scope.row)"
            >
              选择
            </el-button>
            <el-button
              v-else
              type="text"
              size="small"
              class="text-gray"
              disabled
            >
              已选
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页 -->
    <div class="pagination-section">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        :page-size="pageSize"
        :page-sizes="[10, 15, 20, 30, 50]"
        :current-page.sync="currentPage"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>

    <!-- 操作区（仅多选模式显示） -->
    <div v-if="multiple" slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleConfirm">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
/**
 * 班级选择器组件
 *
 * 功能说明：
 * - 支持单选和多选模式
 * - 支持筛选：校区、课程分类、学年学期、班级名称、班级状态
 * - 支持双击行快速选择
 * - 已选班级行高亮显示
 * - 表格展示：班级名称、课程名称、授课教师、校区、上课时间、状态、名额进度
 *
 * Props:
 * - visible: Boolean - 控制弹窗显示/隐藏
 * - selectedId: Number - 当前已选班级 ID（单选模式）
 * - multiple: Boolean - 是否多选模式（默认 false）
 * - defaultFilters: Object - 默认筛选条件
 *
 * Events:
 * - success: 选择成功回调
 * - update:visible: 支持同步修饰符
 */
export default {
  name: 'SelectClass',

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    selectedId: {
      type: Number,
      default: 0
    },
    multiple: {
      type: Boolean,
      default: false
    },
    defaultFilters: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      dialogVisible: false,
      tableLoading: false,
      tableData: [],
      currentPage: 1,
      pageSize: 15,
      total: 0,

      // 筛选表单
      filterForm: {
        schoolAreaId: 0,
        name: ''
      },

      // 下拉选项数据
      schoolAreaList: [],

      // 已选班级列表（多选模式）
      selectedList: []
    }
  },

  computed: {
    // 已选班级 ID 列表
    selectedIds() {
      return this.selectedList.map(item => item.id)
    }
  },

  activated() {
    this.dialogVisible = false
    this.$emit('update:visible', false)
  },

  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.dialogVisible = val
        if (val) {
          this.init()
        }
      }
    },

    dialogVisible(val) {
      this.$emit('update:visible', val)
    },

    // 监听默认筛选条件变化
    defaultFilters: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val && Object.keys(val).length > 0) {
          Object.assign(this.filterForm, val)
        }
      }
    }
  },

  methods: {
    /**
     * 初始化
     */
    async init() {
      await this.getSchoolAreaList()
      this.getClassList()
    },

    /**
     * 获取校区列表
     */
    async getSchoolAreaList() {
      try {
        const res = await this.$api.rGet('school/area/all')
        if (res.status === 1) {
          this.schoolAreaList = res.data || []
        }
      } catch (error) {
        console.error('获取校区列表失败:', error)
      }
    },

    /**
     * 获取班级列表
     */
    async getClassList() {
      this.tableLoading = true
      try {
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize,
          schoolAreaId: this.filterForm.schoolAreaId || undefined,
          name: this.filterForm.name ? encodeURIComponent(this.filterForm.name) : undefined
        }

        // 移除 undefined 值
        Object.keys(params).forEach(key => {
          if (params[key] === undefined) {
            delete params[key]
          }
        })

        const res = await this.$api.rGet('course/class', params)
        if (res.status === 1) {
          this.tableData = res.data.data || []
          this.total = res.data.total || 0
          this.currentPage = res.data.current_page || 1

          // 恢复表格选中状态（多选模式）
          this.$nextTick(() => {
            if (this.multiple && this.$refs.classTable) {
              this.tableData.forEach(row => {
                if (this.selectedIds.includes(row.id)) {
                  this.$refs.classTable.toggleRowSelection(row, true)
                }
              })
            }
          })
        }
      } catch (error) {
        console.error('获取班级列表失败:', error)
        this.$message.error('获取班级列表失败')
      } finally {
        this.tableLoading = false
      }
    },

    /**
     * 搜索
     */
    handleSearch() {
      this.currentPage = 1
      this.getClassList()
    },

    /**
     * 重置筛选条件
     */
    handleReset() {
      this.filterForm = {
        schoolAreaId: 0,
        name: ''
      }
      // 应用默认筛选条件
      if (this.defaultFilters && Object.keys(this.defaultFilters).length > 0) {
        Object.assign(this.filterForm, this.defaultFilters)
      }
      this.handleSearch()
    },

    /**
     * 分页大小变化
     */
    handleSizeChange(val) {
      this.pageSize = val
      this.getClassList()
    },

    /**
     * 页码变化
     */
    handlePageChange(page) {
      this.currentPage = page
      this.getClassList()
    },

    /**
     * 判断是否已选中
     */
    isSelected(row) {
      if (this.multiple) {
        return this.selectedIds.includes(row.id)
      }
      return this.selectedId === row.id
    },

    /**
     * 计算名额百分比
     */
    getQuotaPercentage(row) {
      const enrollCount = row.enrollCount || 0
      const studentCount = row.studentCount || 0
      if (enrollCount === 0) return 0
      return Math.min(Math.round((studentCount / enrollCount) * 100), 100)
    },

    /**
     * 获取名额进度条颜色
     */
    getQuotaColor(row) {
      const percentage = this.getQuotaPercentage(row)
      if (percentage >= 100) return '#f56c6c'
      if (percentage >= 80) return '#e6a23c'
      return '#67c23a'
    },

    /**
     * 选择班级（单选模式）
     */
    handleSelect(row) {
      if (this.multiple) {
        // 多选模式
        const index = this.selectedIds.indexOf(row.id)
        if (index === -1) {
          this.selectedList.push(row)
          this.$refs.classTable.toggleRowSelection(row, true)
        } else {
          this.selectedList.splice(index, 1)
          this.$refs.classTable.toggleRowSelection(row, false)
        }
      } else {
        // 单选模式
        this.$emit('success', row)
        this.handleClose()
      }
    },

    /**
     * 双击行选择
     */
    handleRowDblClick(row) {
      this.handleSelect(row)
    },

    /**
     * 表格选择变化（多选模式）
     */
    handleSelectionChange(selection) {
      if (this.multiple) {
        // 保留不在当前页的已选项
        const currentPageIds = this.tableData.map(item => item.id)
        const notCurrentPageSelected = this.selectedList.filter(
          item => !currentPageIds.includes(item.id)
        )
        this.selectedList = [...notCurrentPageSelected, ...selection]
      }
    },

    /**
     * 移除已选班级（多选模式）
     */
    removeSelected(row) {
      const index = this.selectedIds.indexOf(row.id)
      if (index !== -1) {
        this.selectedList.splice(index, 1)
        // 取消表格选中状态
        const tableRow = this.tableData.find(item => item.id === row.id)
        if (tableRow && this.$refs.classTable) {
          this.$refs.classTable.toggleRowSelection(tableRow, false)
        }
      }
    },

    /**
     * 清空选择（多选模式）
     */
    clearSelection() {
      this.selectedList = []
      if (this.$refs.classTable) {
        this.$refs.classTable.clearSelection()
      }
    },

    /**
     * 确认选择
     */
    handleConfirm() {
      if (this.multiple) {
        this.$emit('success', this.selectedList)
      } else {
        if (this.tableData.length > 0) {
          const selectedRow = this.tableData.find(row => row.id === this.selectedId)
          if (selectedRow) {
            this.$emit('success', selectedRow)
          } else {
            this.$message.warning('请选择一个班级')
            return
          }
        }
      }
      this.handleClose()
    },

    /**
     * 关闭弹窗
     */
    handleClose() {
      this.dialogVisible = false
      // 重置选择状态
      if (!this.multiple) {
        this.selectedList = []
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-section {
  margin-bottom: 16px;
  padding: 16px;
  background-color: #f5f7fa;
  border-radius: 4px;

  .el-form-item {
    margin-bottom: 8px;
  }
}

.selected-section {
  margin-bottom: 16px;
  padding: 12px 16px;
  background-color: #f0f9ff;
  border: 1px solid #bfdbfe;
  border-radius: 4px;

  .selected-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;

    .selected-title {
      font-size: 14px;
      font-weight: 500;
      color: #1e40af;
    }
  }

  .selected-tags {
    .el-tag {
      margin-right: 8px;
      margin-bottom: 8px;
    }
  }
}

.table-section {
  margin-bottom: 16px;

  .quota-cell {
    display: flex;
    flex-direction: column;
    align-items: center;

    .el-progress {
      width: 100%;
      margin-bottom: 4px;
    }

    .quota-text {
      font-size: 12px;
      color: #606266;
    }
  }
}

.pagination-section {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.dialog-footer {
  text-align: right;
}

.text-gray {
  color: #909399;
}
</style>



// WEBPACK FOOTER //
// src/components/SelectClass.vue